import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import {
  buildInitialFormValues,
  computeFieldErrors,
} from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.utils';
import { ButtonGroupWrapper } from 'app/shared/componentsRestyled/coreDesignSystem/Buttons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { CUSTOM_FIELD_TARGET } from 'constants/customFieldTargets';
import { LoanActionRequestPayloadType } from 'constants/loanActionEditor';
import { HORIZONTAL_ALIGNMENT } from 'constants/ui';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { getConditionalArray } from 'utils/data';

import { closeDialogAction, showErrorMessageAction, showMessageAction } from 'store/global/actions';
import {
  getApplicationBorrowersAction,
  getApplicationIncomesAction,
  getBorrowersReoAction,
  getLoanAssetsAction,
  getPropertiesAction,
  getSubjectPropertyAction,
} from 'store/loanApplications/actions';
import { loadCustomFieldValuesAction } from 'store/loanApplications/actions/customFieldValues.actions';
import {
  selectAssetsWithTypeAndAmount,
  selectExtendedBorrowers,
  selectExtendedNonBorrowerEntitiesCommonInfo,
  selectIncomesWithTypeAndAmount,
  selectLiabilitiesValues,
  selectPropertyValues,
  selectSubjectProperty,
} from 'store/loanApplications/selectors';
import { selectCustomFieldValuesByApplicationId } from 'store/loanApplications/selectors/customFieldValues.selectors';
import { selectCustomFields } from 'store/system/selectors/customFieldDefinitions.selectors';

import { executeManualLoanAction } from '../../hooks/useLoanActionsControls/utils';

import EnterCustomFieldsModalTargetGroup from './components/EnterCustomFieldsModalTargetGroup';
import { EMPTY_MODAL_MESSAGE, SUBMIT_SUCCESS_MESSAGE } from './EnterCustomFieldsModal.constants';
import {
  emptyStateStyles,
  footerStyles,
  loadingStateStyles,
  modalBodyStyles,
} from './EnterCustomFieldsModal.styles';
import { EnterCustomFieldsModalTestIds } from './EnterCustomFieldsModal.testIds';
import {
  buildModalSectionGroups,
  buildSubmitPayload,
  flattenSectionGroups,
  getRequiredCustomFieldTargets,
} from './EnterCustomFieldsModal.utils';

const EnterCustomFieldsModal = ({
  dialogId,
  applicationId,
  loanActionId,
  customFieldDefinitionIds,
  executor = executeManualLoanAction,
  onSubmitSuccess = null,
}) => {
  const dispatch = useDispatch();

  const [formValuesByKey, setFormValuesByKey] = useState({});
  const [touchedByKey, setTouchedByKey] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const lastInitialFormValuesByKeyRef = useRef({});

  const allDefinitions = useSelector(selectCustomFields);
  const customFieldValuesSelector = useMemo(
    () => selectCustomFieldValuesByApplicationId(applicationId),
    [applicationId],
  );
  const customFieldValues = useSelector(customFieldValuesSelector);
  const borrowers = useSelector(selectExtendedBorrowers);
  const subjectProperty = useSelector(selectSubjectProperty);
  const properties = useSelector(selectPropertyValues);
  const incomes = useSelector(selectIncomesWithTypeAndAmount);
  const assets = useSelector(selectAssetsWithTypeAndAmount);
  const liabilities = useSelector(selectLiabilitiesValues);
  const nbes = useSelector(selectExtendedNonBorrowerEntitiesCommonInfo);

  const sectionGroups = useMemo(
    () =>
      buildModalSectionGroups({
        definitions: allDefinitions,
        selectedIds: customFieldDefinitionIds,
        applicationId,
        subjectProperty,
        borrowers,
        properties,
        incomes,
        assets,
        liabilities,
        nbes,
      }),
    [
      allDefinitions,
      customFieldDefinitionIds,
      applicationId,
      subjectProperty,
      borrowers,
      properties,
      incomes,
      assets,
      liabilities,
      nbes,
    ],
  );

  const sections = useMemo(() => flattenSectionGroups(sectionGroups), [sectionGroups]);

  const initialFormValuesByKey = useMemo(
    () =>
      _.fromPairs(
        sections.map(section => [
          section.key,
          buildInitialFormValues({
            definitions: section.definitions,
            values: customFieldValues,
            target: section.target,
            targetEntityId: section.entityId,
          }),
        ]),
      ),
    [sections, customFieldValues],
  );

  const allErrorsByKey = useMemo(
    () =>
      _.fromPairs(
        sections.map(section => [
          section.key,
          computeFieldErrors({
            definitions: section.definitions,
            formValues: formValuesByKey[section.key] ?? initialFormValuesByKey[section.key],
          }),
        ]),
      ),
    [sections, formValuesByKey, initialFormValuesByKey],
  );

  const visibleErrorsByKey = useMemo(
    () =>
      _.mapValues(allErrorsByKey, (sectionErrors, sectionKey) =>
        _.pickBy(sectionErrors, (_error, definitionId) => touchedByKey[sectionKey]?.[definitionId]),
      ),
    [allErrorsByKey, touchedByKey],
  );

  const isValid = useMemo(() => _.every(allErrorsByKey, _.isEmpty), [allErrorsByKey]);

  const requiredTargets = useMemo(
    () =>
      getRequiredCustomFieldTargets({
        definitions: allDefinitions,
        selectedIds: customFieldDefinitionIds,
      }),
    [allDefinitions, customFieldDefinitionIds],
  );

  const requiredTargetsKey = requiredTargets.join('|');

  const onFieldChange = useCallback(({ sectionKey, definitionId, value }) => {
    setFormValuesByKey(prev => {
      const current = prev[sectionKey] ?? {};

      return { ...prev, [sectionKey]: { ...current, [definitionId]: value } };
    });
    setTouchedByKey(prev => {
      const current = prev[sectionKey] ?? {};

      if (current[definitionId]) {
        return prev;
      }

      return { ...prev, [sectionKey]: { ...current, [definitionId]: true } };
    });
  }, []);

  const onFieldBlur = useCallback(({ sectionKey, definitionId }) => {
    setTouchedByKey(prev => {
      const current = prev[sectionKey] ?? {};

      if (current[definitionId]) {
        return prev;
      }

      return { ...prev, [sectionKey]: { ...current, [definitionId]: true } };
    });
  }, []);

  const markAllTouched = useCallback(() => {
    setTouchedByKey(() =>
      _.fromPairs(
        sections.map(section => [
          section.key,
          _.fromPairs(section.definitions.map(definition => [definition.id, true])),
        ]),
      ),
    );
  }, [sections]);

  const onClose = useCallback(() => {
    dispatch(closeDialogAction(dialogId));
  }, [dispatch, dialogId]);

  const onSubmit = useCallback(async () => {
    if (!isValid) {
      markAllTouched();

      return;
    }

    setIsSubmitting(true);

    const payload = {
      applicationId,
      loanActionId,
      metadata: {
        type: LoanActionRequestPayloadType.CustomFieldValues,
        customFields: buildSubmitPayload({ sections, formValuesByKey }),
      },
    };

    try {
      await executor(payload);

      await dispatch(loadCustomFieldValuesAction({ applicationId }));

      dispatch(showMessageAction({ message: SUBMIT_SUCCESS_MESSAGE, variant: 'success' }));
      dispatch(closeDialogAction(dialogId));
      onSubmitSuccess?.();
    } catch (error) {
      const message =
        error?.response?.data?.message ?? error?.message ?? 'Failed to save custom field values.';

      dispatch(showErrorMessageAction({ message }));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    applicationId,
    dialogId,
    dispatch,
    executor,
    formValuesByKey,
    isValid,
    loanActionId,
    markAllTouched,
    onSubmitSuccess,
    sections,
  ]);

  useEffectSafeStrictMode(() => {
    if (!applicationId) {
      return;
    }

    const targets = new Set(requiredTargets);
    const shouldLoadBorrowers =
      targets.has(CUSTOM_FIELD_TARGET.BORROWER) || targets.has(CUSTOM_FIELD_TARGET.INCOME);

    const loaders = [
      dispatch(loadCustomFieldValuesAction({ applicationId })),
      ...getConditionalArray(shouldLoadBorrowers, () => [
        dispatch(getApplicationBorrowersAction({ applicationId })),
      ]),
      ...getConditionalArray(targets.has(CUSTOM_FIELD_TARGET.SUBJECT_PROPERTY), () => [
        dispatch(getSubjectPropertyAction({ applicationId })),
      ]),
      ...getConditionalArray(targets.has(CUSTOM_FIELD_TARGET.PROPERTY), () => [
        dispatch(getPropertiesAction({ applicationId })),
      ]),
      ...getConditionalArray(targets.has(CUSTOM_FIELD_TARGET.INCOME), () => [
        dispatch(getApplicationIncomesAction({ applicationId })),
      ]),
      ...getConditionalArray(targets.has(CUSTOM_FIELD_TARGET.ASSET), () => [
        dispatch(getLoanAssetsAction({ applicationId })),
      ]),
      ...getConditionalArray(targets.has(CUSTOM_FIELD_TARGET.LIABILITY), () => [
        dispatch(getBorrowersReoAction({ applicationId })),
      ]),
    ];

    setIsLoadingData(true);

    Promise.allSettled(loaders).then(() => setIsLoadingData(false));
  }, [applicationId, dispatch, requiredTargetsKey]);

  useEffect(() => {
    const lastInitial = lastInitialFormValuesByKeyRef.current;

    setFormValuesByKey(prev => {
      const next = _.mapValues(initialFormValuesByKey, (initialValues, key) => {
        const previousValues = prev[key];
        const lastInitialValues = lastInitial[key];
        const isUntouched = _.isNil(previousValues) || _.isEqual(previousValues, lastInitialValues);

        return isUntouched ? initialValues : previousValues;
      });

      return _.isEqual(prev, next) ? prev : next;
    });

    lastInitialFormValuesByKeyRef.current = initialFormValuesByKey;
  }, [initialFormValuesByKey]);

  const hasSections = sections.length > 0;
  const isSubmitDisabled = !isValid || isSubmitting || isLoadingData || !hasSections;

  const renderBody = () => {
    if (isLoadingData) {
      return (
        <Box sx={loadingStateStyles}>
          <CircularProgress size={32} />
        </Box>
      );
    }

    if (!hasSections) {
      return (
        <Box sx={emptyStateStyles}>
          <Typography color="text.secondary" component="p" variant="regular14">
            {EMPTY_MODAL_MESSAGE}
          </Typography>
        </Box>
      );
    }

    return sectionGroups.map(group => (
      <EnterCustomFieldsModalTargetGroup
        formValuesByKey={formValuesByKey}
        groupHeader={group.groupHeader}
        initialFormValuesByKey={initialFormValuesByKey}
        isDisabled={isSubmitting}
        key={group.key}
        sections={group.sections}
        visibleErrorsByKey={visibleErrorsByKey}
        onFieldBlur={onFieldBlur}
        onFieldChange={onFieldChange}
      />
    ));
  };

  return (
    <Box sx={modalBodyStyles}>
      {renderBody()}
      <ButtonGroupWrapper alignment={HORIZONTAL_ALIGNMENT.RIGHT} sx={footerStyles}>
        <Button
          data-testid={EnterCustomFieldsModalTestIds.CANCEL_BUTTON}
          disabled={isSubmitting}
          size="large"
          variant="line"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          data-testid={EnterCustomFieldsModalTestIds.SUBMIT_BUTTON}
          disabled={isSubmitDisabled}
          size="large"
          onClick={onSubmit}
        >
          Submit
        </Button>
      </ButtonGroupWrapper>
    </Box>
  );
};

EnterCustomFieldsModal.propTypes = {
  dialogId: PropTypes.string.isRequired,
  applicationId: PropTypes.string.isRequired,
  loanActionId: PropTypes.string.isRequired,
  customFieldDefinitionIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  executor: PropTypes.func,
  onSubmitSuccess: PropTypes.func,
};

export default memo(EnterCustomFieldsModal);
